import React, { memo, useCallback, useMemo, useState } from 'react'
import AboutPanel from './AboutPanel'
import LocationPanel from './LocationPanel'
import TreatmentPanel from './TreatmentPanel'
import { ChevronDown } from 'react-bootstrap-icons'
import { debounce } from 'lodash'
import { Department } from '@/domain/department'
import IconHeartNav from '@/components/icons/IconHeartNav/IconHeartNav'
import { navigate } from 'gatsby'

const DEBOUNCE_DELAY_MS = 200

const links = [
  {
    label: 'Addiction',
    component: LocationPanel
  },
  {
    label: 'Mental Health',
    component: TreatmentPanel
  },
  {
    label: 'About Us',
    component: AboutPanel
  }
] as const

type Menu = {
  label: string
  component: React.ComponentType
}

function DesktopNav({ department }: { department: Department }) {
  const [hoveredMenu, setHoveredMenu] = useState<Menu | null>(null)
  const [showMegaMenu, setShowMegaMenu] = useState<boolean>(false)

  const navLinks = useMemo(() => {
    return links.map((link) => {
      if (
        link.label === 'Addiction' &&
        department === Department.MentalHealth
      ) {
        return {
          ...link,
          label: 'Treatment'
        }
      }
      return link
    })
  }, [department])

  const debouncedShow = useMemo(
    () =>
      debounce((value: boolean) => {
        setShowMegaMenu(value)
      }, DEBOUNCE_DELAY_MS),
    []
  )

  React.useEffect(() => {
    return () => {
      debouncedShow.cancel()
    }
  }, [debouncedShow])

  const handleMouseOut = useCallback(() => {
    debouncedShow(false)
  }, [debouncedShow])

  const handleMouseOver = useCallback(() => {
    debouncedShow.cancel()
    setShowMegaMenu(true)
  }, [debouncedShow])

  const handleItemMouseOver = useCallback(
    (item: Menu) => {
      setHoveredMenu(item)
      debouncedShow.cancel()
      debouncedShow(true)
    },
    [debouncedShow]
  )

  return (
    <>
      <ul
        className="hidden justify-end gap-2 md:inline-flex"
        onMouseOut={() => {
          handleMouseOut()
        }}
      >
        {navLinks.map((item) => {
          const isHovered = hoveredMenu?.label === item.label && showMegaMenu
          return (
            <li
              key={item.label}
              className="flex cursor-pointer items-center"
              onMouseOver={() => handleItemMouseOver(item)}
            >
              <div className="mx-1.5 flex items-center gap-2">
                <div className="relative my-2 inline-block">
                  {item.label}
                  {isHovered && (
                    <div className="absolute bottom-0 h-px w-full bg-gray-1100" />
                  )}
                </div>
                <ChevronDown className="size-3 shrink-0 text-gray-1100" />
              </div>
            </li>
          )
        })}
        <li className="flex items-center">
          <IconHeartNav
            className="relative my-2 inline-block"
            onClick={() => navigate('/favorite')}
          />
        </li>
      </ul>
      <div
        className={`transition-all duration-200 ease-in-out ${
          showMegaMenu ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        {navLinks.map((item) => {
          const isVisible = hoveredMenu?.label === item.label
          return (
            <div
              key={item.label}
              className={`absolute left-0 top-0 z-50 min-w-full ${
                isVisible
                  ? 'visible opacity-100'
                  : 'pointer-events-none invisible opacity-0'
              }`}
            >
              <item.component />
            </div>
          )
        })}
      </div>
    </>
  )
}

export default memo(DesktopNav)
